import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Divider,
  Typography,
  Box,
  Chip,
  Collapse,
} from '@mui/material';
import {
  Person as PersonIcon,
  Cloud as CloudIcon,
  SwapHoriz as SwitchIcon,
  Edit as EditIcon,
  Logout as LogoutIcon,
  MenuBook as MenuBookIcon,
  Science as ScienceIcon,
  Build as BuildIcon,
  Storage as StorageIcon,
  Rule as RuleIcon,
  Group as GroupIcon,
  HelpOutline as HelpOutlineIcon,
  Palette as PaletteIcon,
  DeveloperBoard as DeveloperBoardIcon,
  Inventory as InventoryIcon,
  RocketLaunch as RocketLaunchIcon,
  AccountTree as AccountTreeIcon,
  Assessment as AssessmentIcon,
  ConfirmationNumber as ConfirmationNumberIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useAuth } from '../../auth/context/AuthContext';
import { useThemeMode } from '../../../theme';
import ThemePickerModal, { themeOptions } from './ThemePickerModal';
import CustomerManagementModal from '../../customer/components/CustomerManagementModal';
import { PromptManagementModal } from '../../prompt';
import { PromptStudioModal } from '../../prompt-studio';
import { useFeatureSwitch } from '../../../shared/hooks/useFeatureSwitch';
import CustomerWorkflowRunsModal from '../../workflow/components/CustomerWorkflowRunsModal';
import { ToolManagerModal } from '../../toolset';
import { CustomerKBModal } from '../../knowledge';
import { EventManagementModal } from '../../event-management';
import { UserManagementModal, PortalUserManagementModal } from '../../user-management';
import UserProfileModal from './UserProfileModal';
import { HelpModal } from '../../help';
import { KernelModal } from '../../kernel';
import CatalogManagementModal from '../../service-administration/components/CatalogManagementModal';
import CustomerDeploymentsModal from '../../service-administration/components/CustomerDeploymentsModal';
import { ReportsModal } from '../../reports';
import { TicketManagementModal } from '../../itsm';
import { Customer } from '../../../types';
import { LEVEL_LABELS } from '../../../shared/constants/authorizationGroups';
import { viewportGutterMaxWidth } from '../../../shared/constants/layout';

type ModalName = 'customers' | 'prompts' | 'promptstudio' | 'toolmanager' | 'kb' | 'events' | 'tickets' | 'users' | 'portalUsers' | 'profile' | 'help' | 'kernel' | 'catalog' | 'deployments' | 'reports' | 'workflowRuns' | 'theme';
type ModalAction = { type: 'open'; modal: ModalName } | { type: 'close' };
type ModalState = { active: ModalName | null };

function modalReducer(_state: ModalState, action: ModalAction): ModalState {
  switch (action.type) {
    case 'open': return { active: action.modal };
    case 'close': return { active: null };
  }
}

const themeLabels = Object.fromEntries(
  themeOptions.map((o) => [o.value, o.label]),
) as Record<string, string>;

interface UserMenuProps {
  selectedCustomer: Customer | null;
  onManageAccounts: () => void;
  onCustomerUpdated?: () => void;
  onInvestigationCreated?: (investigation: import('../../../types').Investigation, initialMessage?: string) => void;
}

const UserMenu: React.FC<UserMenuProps> = ({
  selectedCustomer,
  onManageAccounts,
  onCustomerUpdated,
  onInvestigationCreated,
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { customerId: routeCustomerId } = useParams<{ customerId: string }>();
  const actionItemParam = searchParams.get('actionItem');
  const reportParam = searchParams.get('report');
  // The selected customer lags the route while a switch loads — InvestigationContext
  // keeps the previous customer rendered on purpose — so a report id taken from the
  // new URL must not be resolved against the customer still on screen.
  const reportParamCustomer = selectedCustomer && selectedCustomer.id === routeCustomerId
    ? reportParam
    : null;
  const { user, logout, viewAsRole, setViewAsRole, isAdmin } = useAuth();
  const { themeName } = useThemeMode();
  const studioEnabled = useFeatureSwitch('prompt_studio');
  // The one place this screen reads the switch. Its reads stay available while the switch is
  // off, so the entry is hidden rather than the data being withheld: an operator who has not
  // been released the feature has no run history to go looking for.
  const workflowsEnabled = useFeatureSwitch('workflows');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [modals, dispatchModal] = useReducer(modalReducer, { active: null });
  const open = Boolean(anchorEl);
  const pendingActionRef = useRef<(() => void) | null>(null);
  const [openSections, setOpenSections] = useState<Record<'customer' | 'admin' | 'settings', boolean>>({
    customer: true,
    admin: false,
    settings: false,
  });

  const toggleSection = (id: 'customer' | 'admin' | 'settings') => (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenSections((cur) => ({ ...cur, [id]: !cur[id] }));
  };

  useEffect(() => {
    return () => { pendingActionRef.current = null; };
  }, []);

  // Deep link: a ?actionItem=oi-... param (e.g. from a Freshservice ticket) opens
  // Event Management on the Action Log with that action log item selected.
  useEffect(() => {
    if (actionItemParam && selectedCustomer) {
      dispatchModal({ type: 'open', modal: 'events' });
    }
  }, [actionItemParam, selectedCustomer]);

  const clearActionItemParam = useCallback(() => {
    if (!actionItemParam) return;
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('actionItem');
      return next;
    }, { replace: true });
  }, [actionItemParam, setSearchParams]);

  // Deep link: a ?report=oi-... param (from a report-ready notification email)
  // opens Reports with that report selected.
  useEffect(() => {
    if (reportParamCustomer) {
      dispatchModal({ type: 'open', modal: 'reports' });
    }
  }, [reportParamCustomer]);

  const clearReportParam = useCallback(() => {
    if (!reportParam) return;
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('report');
      return next;
    }, { replace: true });
  }, [reportParam, setSearchParams]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setOpenSections({ customer: true, admin: false, settings: false });
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const closeAndThen = (action: () => void) => {
    pendingActionRef.current = action;
    handleClose();
  };

  const handleMenuExited = useCallback(() => {
    pendingActionRef.current?.();
    pendingActionRef.current = null;
  }, []);

  const openModal = (modal: ModalName) => () => closeAndThen(() => dispatchModal({ type: 'open', modal }));
  const handleManageCustomers = openModal('customers');
  const handleManageUsers = openModal('users');
  const handleManageEventManagement = openModal('events');
  const handleManagePrompts = openModal('prompts');
  const handleManageKB = openModal('kb');
  const handleProfile = openModal('profile');
  const handleKernel = openModal('kernel');
  const handleHelp = openModal('help');

  const handleManageAccounts = () => {
    if (selectedCustomer) {
      onManageAccounts();
    }
    handleClose();
  };

  const handleLogout = () => {
    handleClose();
    logout();
  };

  if (!user) {
    return null;
  }

  const renderSectionHeader = (id: 'customer' | 'admin' | 'settings', label: string) => (
    <Box
      key={`section-header-${id}`}
      onClick={toggleSection(id)}
      sx={{
        pl: 1,
        pr: 2,
        py: 0.75,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        cursor: 'pointer',
        userSelect: 'none',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      {openSections[id]
        ? <ExpandMoreIcon fontSize="small" sx={{ opacity: 0.6 }} />
        : <ChevronRightIcon fontSize="small" sx={{ opacity: 0.6 }} />}
      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary', opacity: 0.6, letterSpacing: 0.5 }}>
        {label}
      </Typography>
    </Box>
  );

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {viewAsRole && (
          <Chip
            label={`Viewing as: ${LEVEL_LABELS[viewAsRole]}`}
            color="warning"
            size="small"
            variant="outlined"
            onDelete={() => setViewAsRole(null)}
          />
        )}
        <IconButton
          onClick={handleClick}
          size="small"
          sx={{ ml: viewAsRole ? 0 : 2 }}
          aria-controls={open ? 'user-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          <Avatar sx={{ width: 32, height: 32, bgcolor: viewAsRole ? 'warning.main' : 'primary.main' }}>
            {(user.name || user.email).charAt(0).toUpperCase()}
          </Avatar>
        </IconButton>
      </Box>

      <Menu
        anchorEl={anchorEl}
        id="user-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 3,
          sx: {
            overflow: 'auto',
            maxHeight: 'calc(100vh - 80px)',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            minWidth: 280,
            maxWidth: viewportGutterMaxWidth,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        TransitionProps={{ onExited: handleMenuExited }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ bgcolor: viewAsRole ? 'warning.main' : 'primary.main' }}>
              {(user.name || user.email).charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {user.name || user.email}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user.email}
              </Typography>
            </Box>
          </Box>
        </Box>

        {renderSectionHeader('customer', 'CURRENT CUSTOMER')}
        <Collapse in={openSections.customer} timeout="auto" unmountOnExit sx={{ '& .MuiMenuItem-root': { pl: 4 } }}>
          <MenuItem onClick={() => {
            handleClose();
            navigate('/?switch=true');
          }}>
            <ListItemIcon>
              <SwitchIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Switch Customer" />
          </MenuItem>

          <MenuItem onClick={handleManageAccounts}>
            <ListItemIcon>
              <CloudIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="AWS Accounts" />
          </MenuItem>

          {selectedCustomer && (
            <MenuItem onClick={openModal('tickets')}>
              <ListItemIcon>
                <ConfirmationNumberIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Tickets" />
            </MenuItem>
          )}

          <MenuItem onClick={handleManageEventManagement}>
            <ListItemIcon>
              <RuleIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Event Management" />
          </MenuItem>

          <MenuItem onClick={openModal('reports')}>
            <ListItemIcon>
              <AssessmentIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Reports" />
          </MenuItem>

          {workflowsEnabled && (
            <MenuItem onClick={openModal('workflowRuns')}>
              <ListItemIcon>
                <AccountTreeIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Workflow runs" />
            </MenuItem>
          )}

          <MenuItem onClick={openModal('deployments')}>
            <ListItemIcon>
              <RocketLaunchIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Deployments" />
          </MenuItem>

          <MenuItem onClick={handleManageKB}>
            <ListItemIcon>
              <StorageIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Knowledge Base" />
          </MenuItem>
        </Collapse>

        {isAdmin && [
          <Divider key="admin-divider" />,
          renderSectionHeader('admin', 'ADMIN'),
          <Collapse key="admin-collapse" in={openSections.admin} timeout="auto" unmountOnExit sx={{ '& .MuiMenuItem-root': { pl: 4 } }}>
              <MenuItem onClick={handleManageCustomers}>
                <ListItemIcon>
                  <EditIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Customers" />
              </MenuItem>

              <MenuItem onClick={handleManageUsers}>
                <ListItemIcon>
                  <GroupIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Users" />
              </MenuItem>

              <MenuItem onClick={openModal('portalUsers')}>
                <ListItemIcon>
                  <GroupIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Portal Users" />
              </MenuItem>

              <MenuItem onClick={handleManagePrompts}>
                <ListItemIcon>
                  <MenuBookIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Prompt Manager" />
              </MenuItem>

              {studioEnabled && (
                <MenuItem onClick={openModal('promptstudio')}>
                  <ListItemIcon>
                    <ScienceIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Prompt Studio" />
                </MenuItem>
              )}

              <MenuItem onClick={openModal('toolmanager')}>
                <ListItemIcon>
                  <BuildIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Tool Manager" />
              </MenuItem>

              <MenuItem onClick={handleKernel}>
                <ListItemIcon>
                  <DeveloperBoardIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Kernel" />
              </MenuItem>

              <MenuItem onClick={openModal('catalog')}>
                <ListItemIcon>
                  <InventoryIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Service Catalog" />
              </MenuItem>
          </Collapse>,
        ]}

        <Divider />
        {renderSectionHeader('settings', 'USER SETTINGS')}
        <Collapse in={openSections.settings} timeout="auto" unmountOnExit sx={{ '& .MuiMenuItem-root': { pl: 4 } }}>
          <MenuItem onClick={openModal('theme')}>
            <ListItemIcon><PaletteIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Theme" secondary={themeLabels[themeName]} />
          </MenuItem>

          <MenuItem onClick={handleHelp}>
            <ListItemIcon>
              <HelpOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Help & Documentation" />
          </MenuItem>

          <MenuItem onClick={handleProfile}>
            <ListItemIcon>
              <PersonIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Profile" />
          </MenuItem>
        </Collapse>

        <Divider />

        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText primary="Sign Out" primaryTypographyProps={{ color: 'error' }} />
        </MenuItem>
      </Menu>

      <CustomerManagementModal
        isOpen={modals.active === 'customers'}
        onClose={() => dispatchModal({ type: 'close' })}
        onCustomerUpdated={onCustomerUpdated}
        selectedCustomer={selectedCustomer}
      />

      <PromptManagementModal
        customerId={selectedCustomer?.id}
        isOpen={modals.active === 'prompts'}
        onClose={() => dispatchModal({ type: 'close' })}
      />

      {studioEnabled && (
        <PromptStudioModal
          customerId={selectedCustomer?.id}
          isOpen={modals.active === 'promptstudio'}
          onClose={() => dispatchModal({ type: 'close' })}
        />
      )}

      <ToolManagerModal
        isOpen={modals.active === 'toolmanager'}
        onClose={() => dispatchModal({ type: 'close' })}
      />

      <CustomerKBModal
        isOpen={modals.active === 'kb'}
        customer={selectedCustomer}
        onClose={() => dispatchModal({ type: 'close' })}
      />

      {selectedCustomer && (
        <EventManagementModal
          customerId={selectedCustomer.id}
          isOpen={modals.active === 'events'}
          initialOpsItemId={modals.active === 'events' ? actionItemParam : null}
          onClose={() => { dispatchModal({ type: 'close' }); clearActionItemParam(); }}
          onInvestigationCreated={onInvestigationCreated}
        />
      )}

      <UserManagementModal
        isOpen={modals.active === 'users'}
        onClose={() => dispatchModal({ type: 'close' })}
      />

      {selectedCustomer && (
        <PortalUserManagementModal
          isOpen={modals.active === 'portalUsers'}
          onClose={() => dispatchModal({ type: 'close' })}
          customerId={selectedCustomer.id}
          customerName={selectedCustomer.name}
          customerContactTypes={selectedCustomer.contact_types}
        />
      )}

      <UserProfileModal
        open={modals.active === 'profile'}
        onClose={() => dispatchModal({ type: 'close' })}
      />

      <KernelModal
        isOpen={modals.active === 'kernel'}
        onClose={() => dispatchModal({ type: 'close' })}
      />

      <HelpModal
        open={modals.active === 'help'}
        onClose={() => dispatchModal({ type: 'close' })}
      />

      <CatalogManagementModal
        isOpen={modals.active === 'catalog'}
        onClose={() => dispatchModal({ type: 'close' })}
      />

      <CustomerDeploymentsModal
        customerId={selectedCustomer?.id}
        isOpen={modals.active === 'deployments'}
        onClose={() => dispatchModal({ type: 'close' })}
      />

      <CustomerWorkflowRunsModal
        customerId={selectedCustomer?.id}
        open={modals.active === 'workflowRuns'}
        onClose={() => dispatchModal({ type: 'close' })}
      />

      <ReportsModal
        customerId={selectedCustomer?.id}
        isOpen={modals.active === 'reports'}
        initialOpsItemId={modals.active === 'reports' ? reportParamCustomer : null}
        onClose={() => { dispatchModal({ type: 'close' }); clearReportParam(); }}
      />

      {selectedCustomer && (
        <TicketManagementModal
          customerId={selectedCustomer.id}
          isOpen={modals.active === 'tickets'}
          onClose={() => dispatchModal({ type: 'close' })}
        />
      )}

      <ThemePickerModal
        open={modals.active === 'theme'}
        onClose={() => dispatchModal({ type: 'close' })}
      />
    </>
  );
};

export default UserMenu;
